import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, AutoComplete, Button, DatePicker, Empty, Form, Image, Input, InputNumber, Popconfirm, Select, Spin, Switch, Table, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  HistoryOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import {
  acquireSceneEdit,
  cancelSceneEdit,
  deleteDerivedScenes,
  getSceneBosConfigFileContent,
  getSceneBosConfigFiles,
  getSceneVersions,
  getSceneLabels,
  saveAsScene,
  submitSceneEdit,
  type EventTrigger,
  type SceneBosConfigFileContent,
  type SceneBosConfigFileItem,
  type SceneVersion,
  type SceneEditor,
  type SceneEvent,
  CLOUD_CMD_POLICY_HINT,
  CLOUD_CMD_POLICY_OPTIONS,
  type CloudCmdPolicy,
} from '@/apis/simulation';
import { EventListEditor } from '@/components/EventListEditor';
import { SaveAsModal } from '@/components/SaveAsModal';

const { Title, Text } = Typography;

const ACCENT = '#5e6ad2';
const DATETIME_FMT = 'YYYY-MM-DD HH:mm:ss';

/** scene_change_log 里存的是列名（issue_time 除外亦为 snake_case），统一映射中文标签 */
const FIELD_LABELS: Record<string, string> = {
  scene_start_time: '场景开始时间',
  scene_end_time: '场景结束时间',
  map_name: '地图名称',
  map_version: '地图版本',
  issue_time: '故障时刻',
  label: '场景标签',
};

type RightMode = 'file' | 'edit' | 'history';

/** 后端返回的时间字符串 → Dayjs（不可解析则返回 undefined，表单留空） */
const toDayjs = (s?: string | null): Dayjs | undefined => {
  if (!s) return undefined;
  const d = dayjs(s);
  return d.isValid() ? d : undefined;
};

interface SceneEditFormValues {
  sceneStartTime?: Dayjs;
  sceneEndTime?: Dayjs;
  mapName?: string;
  mapVersion?: string;
  issueTime?: string;
  label?: string;
  openSwitchClosedLoopSec?: number;
  /** 是否屏蔽 finish 信号；bool，默认 false（不屏蔽=正常透传） */
  maskFinishSignal?: boolean;
  /** 云控指令合并策略；默认 passthrough（一起发） */
  cloudCmdPolicy?: CloudCmdPolicy;
  /** 云端控制事件；与 SceneEditFields.events 对齐，undefined/空=无事件 */
  events?: SceneEvent[];
}

const formatSize = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const displayValue = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') return '空';
  return String(v);
};

/**
 * 提交前规整云端控制事件（plan §3.5 / §7 行3）：
 * - 剥除前端 Form.List 行 key（SceneEvent.id），后端不依赖前端 id；
 * - 按 trigger.type 剔除无关字段：time 模式只留 time，position 模式只留 position/radius；
 * - 按 mode 规整 count/frequency：once 带 count(+count>1 时 frequency)，while_inside 带 frequency。
 *
 * 空列表语义由 ``emptyAs`` 区分（events 的 undefined / [] 后端含义不同）：
 * - submit（编辑保存）：删空事件 = 清空，须传 ``[]`` 让后端整体替换为空 → 传 'empty'；
 * - save-as（另存为）：空 = 沿用基准 events，传 ``undefined`` → 传 'undefined'（默认）。
 * 非空列表两者一致，均返回规整后的数组。
 */
const buildSubmitEvents = (
  events?: SceneEvent[],
  emptyAs: 'undefined' | 'empty' = 'undefined',
): SceneEvent[] | undefined => {
  if (!events || events.length === 0) {
    return emptyAs === 'empty' ? [] : undefined;
  }
  return events.map((ev) => {
    const trigger: EventTrigger =
      ev.trigger.type === 'position'
        ? { type: 'position', position: ev.trigger.position, radius: ev.trigger.radius }
        : { type: 'time', time: ev.trigger.time };
    const out: SceneEvent = {
      trigger,
      mode: ev.mode,
      action: { type: ev.action.type, cloudCmd: ev.action.cloudCmd },
    };
    // once:带 count(触发次数)+ frequency(count>1 时多次间隔,count=1 不带)
    // while_inside:带 frequency(区域内持续发送间隔)
    if (ev.mode === 'once') {
      out.count = ev.count;
      if (ev.count && ev.count > 1) out.frequency = ev.frequency;
    } else {
      out.frequency = ev.frequency;
    }
    return out;
  });
};

export default function SceneBosFilesPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { sceneId: sceneIdParam } = useParams<{ sceneId: string }>();
  const [searchParams] = useSearchParams();
  // 返回目的地：generation / compare(任务对比页) 走浏览器后退保留原页状态；
  // management 走带 nodeId 的场景管理页（回到同一场景集合）。
  const fromRaw = searchParams.get('from');
  const from = fromRaw === 'generation' || fromRaw === 'compare' ? fromRaw : 'management';
  // 来自场景管理时携带的树节点 id，返回时用它回到同一场景集合
  const backNodeId = searchParams.get('nodeId');

  const sceneId = sceneIdParam ? Number.parseInt(sceneIdParam, 10) : NaN;

  const [sceneName, setSceneName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [files, setFiles] = useState<SceneBosConfigFileItem[]>([]);
  // 当前场景是否为「另存为」派生场景（后端 external.derived_from）；true 才显示删除按钮
  const [isDerived, setIsDerived] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // 文件内容三态：null=未选 / 加载中；image 走 previewUrl，text 走 content。
  const [fileContent, setFileContent] = useState<SceneBosConfigFileContent | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentTitle, setContentTitle] = useState('');

  // 右侧面板模式：文件内容 / 内联编辑 / 更新记录。
  const [rightMode, setRightMode] = useState<RightMode>('file');

  // ── 编辑锁 ──────────────────────────────────────────────────
  const [editForm] = Form.useForm<SceneEditFormValues>();
  // 响应式监听编辑态 events 数量，给「另存为」提示卡展示带多少条事件
  const watchedEvents = Form.useWatch<SceneEvent[] | undefined>('events', editForm);
  const [editEditor, setEditEditor] = useState<SceneEditor | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  // ── 另存为新场景（F1）──────────────────────────────────────
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsSubmitting, setSaveAsSubmitting] = useState(false);
  // 标签下拉候选项：从后端拉去重后的历史标签，进入编辑态时预加载。
  const [labelOptions, setLabelOptions] = useState<{ value: string }[]>([]);

  // ── 更新记录 ────────────────────────────────────────────────
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<SceneVersion[]>([]);

  const loadList = useCallback(async () => {
    if (Number.isNaN(sceneId)) {
      message.error('无效的场景 ID');
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const data = await getSceneBosConfigFiles(sceneId);
      setSceneName(data?.sceneName ?? '');
      setPrefix(data?.prefix ?? '');
      setFiles(Array.isArray(data?.files) ? data.files : []);
      setIsDerived(!!data?.isDerived);
    } catch {
      /* axios interceptor */
    } finally {
      setListLoading(false);
    }
  }, [sceneId, message]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  /** 离开编辑态时释放自己持有的锁（best-effort）。 */
  const releaseLock = useCallback(() => {
    if (editEditor && !Number.isNaN(sceneId)) {
      cancelSceneEdit(sceneId).catch(() => undefined);
    }
    setEditEditor(null);
  }, [editEditor, sceneId]);

  const openFile = useCallback(
    async (row: SceneBosConfigFileItem) => {
      if (Number.isNaN(sceneId)) return;
      releaseLock();
      setRightMode('file');
      setSelectedKey(row.key);
      setContentLoading(true);
      setFileContent(null);
      setContentTitle(row.name);
      try {
        const res = await getSceneBosConfigFileContent(sceneId, row.key);
        setFileContent(res ?? null);
      } catch {
        setFileContent(null);
      } finally {
        setContentLoading(false);
      }
    },
    [sceneId, releaseLock],
  );

  const handleEdit = useCallback(async () => {
    if (Number.isNaN(sceneId)) return;
    setEditLoading(true);
    try {
      // 抢锁 + 拉当前 6 字段；被他人持有时后端报错，拦截器已弹「xxx 正在修改场景」。
      const data = await acquireSceneEdit(sceneId);
      setEditEditor(data?.editor ?? null);
      editForm.setFieldsValue({
        sceneStartTime: toDayjs(data?.fields?.sceneStartTime),
        sceneEndTime: toDayjs(data?.fields?.sceneEndTime),
        mapName: data?.fields?.mapName ?? '',
        mapVersion: data?.fields?.mapVersion ?? '',
        issueTime: data?.fields?.issueTime ?? '',
        label: data?.fields?.label ?? '',
        // openSwitchClosedLoopSec 后端默认 0；脏值/缺值时回填默认 0 便于用户编辑
        openSwitchClosedLoopSec: data?.fields?.openSwitchClosedLoopSec ?? 0,
        // maskFinishSignal 后端默认 false（不屏蔽）；脏值/缺值回填 false
        maskFinishSignal: data?.fields?.maskFinishSignal ?? false,
        // cloudCmdPolicy 后端缺省 null（等价 passthrough）；回填默认 passthrough
        cloudCmdPolicy: data?.fields?.cloudCmdPolicy ?? 'passthrough',
        // 云端控制事件回填：后端 #38 acquire 回吐 events（plan §3.3/A-1）；
        // 无值给空数组清空上一次编辑态残留，Form.List 自然渲染 0 行。
        events: Array.isArray(data?.fields?.events) ? data?.fields?.events : [],
      });
      setRightMode('edit');
      // best-effort 拉一次候选项，失败静默（用户仍可自由输入）
      getSceneLabels()
        .then((list) => setLabelOptions(list.map((v) => ({ value: v }))))
        .catch(() => undefined);
    } catch {
      /* 冲突 / 场景不存在等：拦截器已提示 */
    } finally {
      setEditLoading(false);
    }
  }, [sceneId, editForm]);

  const handleEditSubmit = useCallback(async () => {
    if (Number.isNaN(sceneId)) return;
    let values: SceneEditFormValues;
    try {
      values = await editForm.validateFields();
    } catch {
      return;
    }
    setEditSubmitting(true);
    try {
      await submitSceneEdit(sceneId, {
        sceneStartTime: values.sceneStartTime?.format(DATETIME_FMT),
        sceneEndTime: values.sceneEndTime?.format(DATETIME_FMT),
        mapName: values.mapName?.trim() || undefined,
        mapVersion: values.mapVersion?.trim() || undefined,
        issueTime: values.issueTime?.trim() || undefined,
        // label 传空串= 清空（后端 noop-if-same 会兜住"原本就空"），非空串= 更新，
        // undefined= 不改；这里始终传"当前表单值"，交给后端判断。
        label: (values.label ?? '').trim(),
        // openSwitchClosedLoopSec: 与现值相同后端 noop;负数统一归一为 -1（开环仿真），
        // 与后端 SceneFieldPatch 校验保持一致。
        openSwitchClosedLoopSec:
          typeof values.openSwitchClosedLoopSec === 'number' && values.openSwitchClosedLoopSec < 0
            ? -1
            : values.openSwitchClosedLoopSec,
        // 是否屏蔽 finish 信号：始终传当前表单值，后端 noop-if-same。
        maskFinishSignal: values.maskFinishSignal ?? false,
        // 云控指令合并策略：始终传当前表单值，后端 noop-if-same。
        cloudCmdPolicy: values.cloudCmdPolicy ?? 'passthrough',
        // 云端控制事件：提交前规整（剥 id + 剔无关字段）；删空 = 清空，传 [] 让后端整体替换
        events: buildSubmitEvents(values.events, 'empty'),
      });
      message.success('保存成功');
      setEditEditor(null); // 后端已释放锁，本地不再重复 cancel
      setRightMode('file');
      // 5 字段任一改动会重写 scene_info.json，刷新文件列表。
      loadList();
    } catch {
      /* 锁失效 / 更新失败：拦截器已提示，保持编辑态让用户重试或取消 */
    } finally {
      setEditSubmitting(false);
    }
  }, [sceneId, editForm, message, loadList]);

  const handleEditCancel = useCallback(() => {
    releaseLock();
    setRightMode('file');
  }, [releaseLock]);

  /**
   * 「另存为新场景」（F1）：复用编辑态 editForm 的 events（single source of
   * truth，不让用户填两遍），叠加 newName + 目标树节点提交。基准场景不变。
   * 成功后释放编辑锁 + 退回文件态；新场景在场景管理树里可见。
   */
  const handleSaveAs = useCallback(
    async (values: { newName?: string; targetTreeNodeId: number }) => {
      if (Number.isNaN(sceneId)) return;
      // 取编辑态当前 events（先 validateFields 保证 radius>0 等行级校验通过）
      let formValues: SceneEditFormValues;
      try {
        formValues = await editForm.validateFields();
      } catch {
        return; // events 校验未过，保持 modal 打开让用户回编辑态改
      }
      setSaveAsSubmitting(true);
      try {
        // 后端返回新场景实体（含自动生成的 name），用它提示更准确
        const created = (await saveAsScene(sceneId, {
          newName: values.newName,
          targetTreeNodeId: values.targetTreeNodeId,
          events: buildSubmitEvents(formValues.events),
        })) as { name?: string } | undefined;
        const newName = created?.name || values.newName || '新场景';
        message.success(`已另存为新场景「${newName}」`);
        setSaveAsOpen(false);
        // 另存为产出独立新场景，基准未改：释放编辑锁 + 退回文件态。
        setEditEditor(null);
        cancelSceneEdit(sceneId).catch(() => undefined);
        setRightMode('file');
      } catch {
        /* axios 拦截器已提示（如非叶子节点 / 重名 / 基准不存在），保持 modal 让用户重试 */
      } finally {
        setSaveAsSubmitting(false);
      }
    },
    [sceneId, editForm, message],
  );

  /**
   * 硬删除当前派生场景（物理删 DB + BOS + 树关联，不可恢复）。
   * 仅在 isDerived 时可触发；成功后返回来源列表页。
   */
  const handleDelete = useCallback(async () => {
    if (Number.isNaN(sceneId)) return;
    setDeleting(true);
    try {
      const res = await deleteDerivedScenes([sceneId]);
      if (res?.affected?.includes(sceneId)) {
        message.success('已删除该派生场景');
        releaseLock();
        // 删除后场景已不存在，返回来源列表
        if (from === 'generation' || from === 'compare') navigate(-1);
        else if (backNodeId) navigate(`/scene-management?nodeId=${backNodeId}`);
        else navigate('/scene-management');
      } else if (res?.notDerived?.includes(sceneId)) {
        message.error('该场景不是派生场景，不能删除');
      } else {
        message.error('删除失败：场景不存在或已被删除');
      }
    } catch {
      /* axios 拦截器已提示 */
    } finally {
      setDeleting(false);
    }
  }, [sceneId, from, backNodeId, navigate, message, releaseLock]);

  const handleShowHistory = useCallback(async () => {
    if (Number.isNaN(sceneId)) return;
    releaseLock();
    setRightMode('history');
    setHistoryLoading(true);
    try {
      // SceneBosFiles 是老编辑页面,只展示 diff 列表,不做分页/回退;拉一页 200 条覆盖大多数场景。
      const page = await getSceneVersions(sceneId, { current: 1, pageSize: 200 });
      setHistoryLogs(page.items);
    } catch {
      setHistoryLogs([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [sceneId, releaseLock]);

  const columns: ColumnsType<SceneBosConfigFileItem> = useMemo(
    () => [
      {
        title: '文件名',
        dataIndex: 'name',
        ellipsis: true,
        render: (name: string, row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FileTextOutlined style={{ color: ACCENT }} />
            <Text style={{ color: selectedKey === row.key && rightMode === 'file' ? ACCENT : undefined }}>
              {name}
            </Text>
          </span>
        ),
      },
      {
        title: '大小',
        dataIndex: 'size',
        width: 110,
        render: (s: number) => formatSize(s ?? 0),
      },
    ],
    [selectedKey, rightMode],
  );

  const panelTitle =
    rightMode === 'edit'
      ? '编辑场景配置'
      : rightMode === 'history'
        ? '更新记录'
        : contentTitle
          ? `内容 · ${contentTitle}`
          : '内容';

  const panelIcon =
    rightMode === 'edit' ? (
      <EditOutlined style={{ color: ACCENT }} />
    ) : rightMode === 'history' ? (
      <HistoryOutlined style={{ color: ACCENT }} />
    ) : (
      <FileTextOutlined style={{ color: ACCENT }} />
    );

  return (
    <div style={{ padding: '24px 32px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            releaseLock();
            // generation / compare：浏览器后退，回到来源页并保留其分页/筛选状态
            if (from === 'generation' || from === 'compare') navigate(-1);
            else if (backNodeId) navigate(`/scene-management?nodeId=${backNodeId}`);
            else navigate('/scene-management');
          }}
        >
          返回
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          场景配置文件
        </Title>
        {sceneName ? (
          <Text type="secondary">
            {sceneName}
            {prefix ? (
              <>
                {' '}
                · <Text code copyable={{ text: prefix }}>{prefix}</Text>
              </>
            ) : null}
          </Text>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {isDerived ? (
            <Popconfirm
              title="删除此派生场景"
              description="将物理删除该场景的数据库记录与 BOS 文件，不可恢复。确定删除？"
              okText="确定删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={handleDelete}
            >
              <Button danger icon={<DeleteOutlined />} loading={deleting} disabled={Number.isNaN(sceneId)}>
                删除此派生场景
              </Button>
            </Popconfirm>
          ) : null}
          <Button
            icon={<HistoryOutlined />}
            type={rightMode === 'history' ? 'primary' : 'default'}
            ghost={rightMode === 'history'}
            disabled={Number.isNaN(sceneId)}
            onClick={handleShowHistory}
          >
            更新记录
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            loading={editLoading}
            disabled={Number.isNaN(sceneId) || rightMode === 'edit'}
            onClick={handleEdit}
          >
            编辑
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* 左：文件列表 */}
        <div
          style={{
            flex: '0 0 380px',
            minWidth: 280,
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
            padding: 12,
            maxHeight: 'calc(100vh - 200px)',
            overflow: 'auto',
          }}
        >
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            文件列表
          </Text>
          {listLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin />
            </div>
          ) : files.length === 0 ? (
            <Empty description="该目录下暂无文件" />
          ) : (
            <Table<SceneBosConfigFileItem>
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={files}
              pagination={false}
              onRow={row => ({
                onClick: () => openFile(row),
                style: { cursor: 'pointer' },
              })}
            />
          )}
        </div>

        {/* 右：文件内容 / 编辑 / 更新记录（同一区域按模式切换） */}
        <div
          style={{
            flex: '1 1 460px',
            minWidth: 320,
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 320,
            maxHeight: 'calc(100vh - 200px)',
            overflow: 'hidden',
          }}
        >
          {/* 面板头：左侧强调条 + 标题；编辑/记录模式右侧给关闭 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span
              style={{
                width: 3,
                height: 16,
                borderRadius: 2,
                background: rightMode === 'file' ? '#d9d9d9' : ACCENT,
              }}
            />
            {panelIcon}
            <Text strong style={{ flex: 1 }} ellipsis>
              {panelTitle}
            </Text>
            {rightMode !== 'file' ? (
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined />}
                onClick={rightMode === 'edit' ? handleEditCancel : () => setRightMode('file')}
              >
                {rightMode === 'edit' ? '取消' : '关闭'}
              </Button>
            ) : null}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
            {rightMode === 'edit'
              ? renderEditPanel()
              : rightMode === 'history'
                ? renderHistoryPanel()
                : renderFilePanel()}
          </div>
        </div>
      </div>
    </div>
  );

  // ── 子渲染 ──────────────────────────────────────────────────

  function renderFilePanel() {
    if (contentLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      );
    }
    if (fileContent === null) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="点击左侧文件查看内容，或点右上角「编辑」修改场景"
          style={{ marginTop: 48 }}
        />
      );
    }
    if (fileContent.viewType === 'image' && fileContent.previewUrl) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Image
            src={fileContent.previewUrl}
            alt={fileContent.name}
            style={{ maxHeight: 'calc(100vh - 300px)', objectFit: 'contain', cursor: 'zoom-in' }}
            preview={{ mask: <span style={{ fontSize: 13 }}>点击查看大图</span> }}
          />
        </div>
      );
    }
    return (
      <pre
        style={{
          margin: 0,
          padding: 12,
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: '#fafafa',
          borderRadius: 6,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        }}
      >
        {fileContent.content}
      </pre>
    );
  }

  function renderEditPanel() {
    return (
      <div style={{ maxWidth: 560 }}>
        {editEditor ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              marginBottom: 16,
              borderRadius: 8,
              background: '#f5f6ff',
              border: `1px solid ${ACCENT}22`,
              fontSize: 13,
            }}
          >
            <UserOutlined style={{ color: ACCENT }} />
            <span>
              当前由 <b>{editEditor.editorName || editEditor.editorKey}</b> 编辑
              {editEditor.lockedAt ? ` · 上锁于 ${editEditor.lockedAt}` : ''}
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                编辑锁 12 小时有效；点「完成」保存并释放，点「取消」放弃修改。
              </Text>
            </span>
          </div>
        ) : null}
        <Form form={editForm} layout="vertical" preserve={false} requiredMark={false}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Form.Item label="场景开始时间" name="sceneStartTime" style={{ flex: '1 1 220px' }}>
              <DatePicker showTime format={DATETIME_FMT} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="场景结束时间" name="sceneEndTime" style={{ flex: '1 1 220px' }}>
              <DatePicker showTime format={DATETIME_FMT} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Form.Item label="地图名称" name="mapName" style={{ flex: '1 1 220px' }}>
              <Input placeholder="map_name" allowClear />
            </Form.Item>
            <Form.Item label="地图版本" name="mapVersion" style={{ flex: '1 1 220px' }}>
              <Input placeholder="map_version" allowClear />
            </Form.Item>
          </div>
          <Form.Item
            label="故障时刻 (issue_time)"
            name="issueTime"
            extra="留空则不修改；与现值相同不调整"
          >
            <Input placeholder="如 2026-06-12 14:30:00" allowClear />
          </Form.Item>
          <Form.Item
            label="开环切闭环秒数 (open_switch_closed_loop_sec, 秒)"
            name="openSwitchClosedLoopSec"
            extra="以仿真开始时间为起点，偏移多少秒作为算法控车时间点；负数统一按 -1（开环仿真）处理；默认 0；改完同步写回 scene_info.json"
          >
            <InputNumber
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0"
              onChange={(v) => {
                // 输入即归一：任意负值实时回显为 -1（开环仿真）
                if (typeof v === 'number' && v < 0) {
                  editForm.setFieldValue('openSwitchClosedLoopSec', -1);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="是否屏蔽 openAPI FINISH 信号 (mask_finish_signal)"
            name="maskFinishSignal"
            valuePropName="checked"
            extra="开启后仿真侧屏蔽 openAPI FINISH 信号（走额外逻辑）；默认关闭（正常透传）；改完同步写回 scene_info.json"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item
            label="云控指令合并策略 (cloud_cmd_policy)"
            name="cloudCmdPolicy"
            extra={CLOUD_CMD_POLICY_HINT}
          >
            <Select options={CLOUD_CMD_POLICY_OPTIONS} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            label="场景标签 (label)"
            name="label"
            extra="下拉可选历史标签，也可直接输入新标签；清空后保存= 移除本场景标签"
          >
            <AutoComplete
              options={labelOptions}
              placeholder="如 绕行 / 变道 / 无保护左转"
              allowClear
              filterOption={(input, option) =>
                (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
              maxLength={255}
            />
          </Form.Item>
          <div style={{ marginTop: 16, borderTop: `1px dashed ${ACCENT}33`, paddingTop: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: ACCENT }}>
                云端控制事件 (cloud_control events)
              </Text>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>
                配置触发条件与绕行动作；不添加则为普通场景。
              </div>
            </div>
            <EventListEditor form={editForm} />
          </div>
        </Form>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button type="primary" loading={editSubmitting} onClick={handleEditSubmit}>
            完成
          </Button>
          <Button
            onClick={() => setSaveAsOpen(true)}
            disabled={editSubmitting || saveAsSubmitting}
          >
            另存为新场景
          </Button>
          <Button onClick={handleEditCancel} disabled={editSubmitting}>
            取消
          </Button>
        </div>
        <SaveAsModal
          open={saveAsOpen}
          baseSceneName={sceneName}
          eventsCount={watchedEvents?.length ?? 0}
          submitting={saveAsSubmitting}
          onCancel={() => setSaveAsOpen(false)}
          onSubmit={handleSaveAs}
        />
      </div>
    );
  }

  function renderHistoryPanel() {
    if (historyLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      );
    }
    if (historyLogs.length === 0) {
      return (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无更新记录" style={{ marginTop: 48 }} />
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {historyLogs.map(log => {
          const entries = Object.entries(log.changedFields || {});
          const who = log.creator || '未知';
          return (
            <div
              key={log.id}
              style={{
                border: '1px solid #f0f0f0',
                borderLeft: `3px solid ${ACCENT}`,
                borderRadius: 8,
                padding: '12px 14px',
                background: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: `${ACCENT}1a`,
                    color: ACCENT,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    flex: '0 0 auto',
                  }}
                >
                  {who.slice(0, 1).toUpperCase()}
                </span>
                <Text strong style={{ fontSize: 13 }}>
                  {who}
                </Text>
                <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                  {log.changeTime || ''}
                </Text>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {entries.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    无字段变化
                  </Text>
                ) : (
                  entries.map(([field, diff]) => (
                    <div
                      key={field}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 8,
                        flexWrap: 'wrap',
                        fontSize: 12,
                      }}
                    >
                      <span
                        style={{
                          flex: '0 0 auto',
                          minWidth: 84,
                          color: '#595959',
                          fontWeight: 500,
                        }}
                      >
                        {FIELD_LABELS[field] || field}
                      </span>
                      <span
                        style={{
                          color: '#bfbfbf',
                          textDecoration: 'line-through',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        }}
                      >
                        {displayValue(diff?.before)}
                      </span>
                      <span style={{ color: '#bfbfbf' }}>→</span>
                      <span
                        style={{
                          color: ACCENT,
                          fontWeight: 600,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        }}
                      >
                        {displayValue(diff?.after)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}
